import { Container, Reveal, SectionHead, BtnLink, PageHero, CheckList } from "../components/site/Primitives";
import { IMG } from "../data/site";
import { DownloadsSection } from "../components/site/Downloads";
import { FlaskConical, Ruler, ClipboardCheck, ShieldCheck, BadgeCheck, Utensils, FileText } from "lucide-react";

const STEPS = [
  ["Dosing", "Controlled resin feed for repeatable shot weight and melt homogeneity."],
  ["Compression molding", "SACMI compression cells form the closure in one efficient cycle."],
  ["Slitting / folding", "Downstream finishing to achieve tamper features and final geometry."],
  ["Cooling", "Stabilised cooling profiles to lock dimensions before release."],
  ["Inspection", "In-line checks and sampling before packing and dispatch."],
];
const QA = [
  [FlaskConical, "Material science", ["Approved resin grades and supplier controls", "Food-contact documentation where required", "Batch traceability from receipt to shipment"]],
  [Ruler, "Measurement & control", ["Dimensional checks against control plans", "Visual and automated inspection on line", "Calibration of gauges and test equipment"]],
  [ClipboardCheck, "Testing protocols", ["Torque and seal performance as per application", "Leak and drop tests for critical SKUs", "Retention of records for audits and claims"]],
];
const CERTS = [
  [ShieldCheck, "ISO 9001:2015 certification", "Management system aligned with international quality standards."],
  [BadgeCheck, "FSSC 22000 certification", "Globally recognized food safety management system certification."],
  [Utensils, "Food-grade compliance", "Materials and processes suitable for intended food-contact applications."],
  [FileText, "Customer & regulatory alignment", "Documentation and controls to support your audits and registrations."],
];
const TECH = [
  ["SACMI CCM48 compression lines", "State-of-the-art SACMI CCM48 compression lines capable of producing over 1 billion closures annually, with exceptional dimensional accuracy and uniform quality."],
  ["Milacron injection & hot runner", "Milacron injection moulding machines with advanced hot runner technology for premium PET preforms — engineered for strength, clarity, and reliable performance on high-speed bottling lines."],
  ["Process control", "Precision-engineered molding systems and refined process control ensure uniform quality, dependable performance, and efficient production across every batch."],
];

export default function Technology() {
  return (
    <div data-testid="technology-page">
      <PageHero testId="technology-hero" eyebrow="Capability" title="Technology & quality" img={IMG("about_facilities_manufacturing-lines")} lead="SACMI compression molding and rigorous quality control ensuring consistent, compliant closures at scale." />

      <section className="py-24 lg:py-32">
        <Container className="grid gap-14 lg:grid-cols-2 lg:gap-20 items-center">
          <div>
            <SectionHead eyebrow="Technology" title="World-class machinery, optimised processes" />
            <div className="mt-10 space-y-8">
              {TECH.map(([t, b], i) => (
                <Reveal key={t} delay={i * 0.08} className="flex gap-5" >
                  <span className="font-display text-sm font-semibold text-brand pt-1">0{i + 1}</span>
                  <div data-testid={`tech-item-${i}`}><h3 className="font-display text-xl font-semibold text-ink">{t}</h3><p className="mt-2 text-[15px] leading-relaxed text-slate-600">{b}</p></div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.1}><div className="img-zoom overflow-hidden aspect-[4/5] lg:aspect-[4/5]"><img src={IMG("about_gallery-production-floor")} alt="Production floor" className="h-full w-full object-cover" /></div></Reveal>
        </Container>
      </section>

      <section className="bg-mist py-24 lg:py-32" data-testid="manufacturing-flow">
        <Container>
          <SectionHead eyebrow="Manufacturing flow" title="From pellet to finished closure" lead="Each stage is controlled and documented so that batches meet your specification." />
          <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map(([t, b], i) => (
              <Reveal key={t} delay={i * 0.07} className="lift bg-white border border-slate-200 p-7 lg:p-8 group">
                <div data-testid={`flow-step-${i + 1}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Step 0{i + 1}</p>
                  <div className="mt-4 h-px w-10 bg-brand transition-all duration-500 group-hover:w-20" />
                  <h3 className="font-display mt-5 text-lg font-semibold text-ink">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-24 lg:py-32">
        <Container>
          <SectionHead eyebrow="Quality assurance" title="Evidence your quality team can rely on" lead="Our laboratory and production controls are structured to give procurement and quality teams the evidence they need." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {QA.map(([Icon, t, items], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div data-testid={`qa-card-${i}`} className="lift h-full border border-slate-200 p-8 lg:p-10">
                  <Icon className="h-7 w-7 text-brand" strokeWidth={1.6} />
                  <h3 className="font-display mt-6 text-xl font-semibold text-ink">{t}</h3>
                  <div className="mt-5"><CheckList items={items} /></div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-mist py-24 lg:py-32">
        <Container>
          <SectionHead eyebrow="Certifications & compliance" title="Certified, audited, documented" lead="Our ISO 9001:2015 and FSSC 22000 certificates, issued by DNV, are available to download below." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CERTS.map(([Icon, t, b], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div data-testid={`cert-card-${i}`} className="lift h-full bg-white border border-slate-200 p-8">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-white"><Icon className="h-6 w-6" strokeWidth={1.6} /></div>
                  <h3 className="font-display mt-6 text-lg font-semibold text-ink">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-t border-slate-300 pt-10">
            <p className="max-w-2xl text-slate-600">Speak with our team about specifications, samples, and line trials.</p>
            <BtnLink to="/contact" testId="technology-contact-btn">Contact us</BtnLink>
          </Reveal>
        </Container>
      </section>
      <DownloadsSection className="" />
    </div>
  );
}
